import { Check, FilterAltOutlined } from "@mui/icons-material";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

export default function ViewportEnvironmentPropsControl(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) {
  const { checked, onChange, onOpenChange } = { ...props };
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isOpen = props.isOpen ?? uncontrolledIsOpen;
  const isControlled = props.isOpen !== undefined;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setOpen = useCallback(
    (nextIsOpen: boolean) => {
      onOpenChange?.(nextIsOpen);
      if (!isControlled) {
        setUncontrolledIsOpen(nextIsOpen);
      }
    },
    [isControlled, onOpenChange]
  );

  useEffect(() => {
    const onDocumentPointerDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [setOpen]);

  const stopEvent = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      ref={rootRef}
      className="relative z-[102]"
      onWheel={stopEvent}
      onMouseDown={stopEvent}
      onClick={stopEvent}
    >
      <button
        aria-label="Environment props"
        className={classNames(
          "h-[30px] w-[30px] rounded-[5px]",
          "flex items-center justify-center cursor-pointer transition-colors",
          isOpen || checked
            ? "bg-creator-primary text-white hover:bg-creator-primary-hover"
            : "bg-transparent text-creator-text-sub hover:bg-creator-primary-washed hover:text-creator-primary"
        )}
        onClick={() => setOpen(!isOpen)}
        type="button"
      >
        <FilterAltOutlined fontSize="small" />
      </button>

      {isOpen && (
        <div
          className={classNames(
            "absolute bottom-[40px] left-0 min-w-[320px] max-w-[380px]",
            "max-h-[520px] overflow-y-auto rounded-[5px] border border-creator-border",
            "bg-creator-surface p-[8px] shadow-xl"
          )}
        >
          <div className="flex flex-col gap-[2px]">
            <label
              className={classNames(
                "flex items-center gap-[14px] px-[14px] py-[12px] rounded-[10px]",
                "text-[18px] font-medium text-creator-text-sub cursor-pointer",
                "hover:bg-creator-primary-washed"
              )}
              onMouseDown={stopEvent}
              onClick={stopEvent}
            >
              <FilterAltOutlined className="text-[32px] text-creator-text-sub" />
              <input
                type="checkbox"
                className="hidden"
                checked={checked}
                onChange={() => onChange(!checked)}
              />
              <span className="flex-1 truncate">Environment Props</span>
              {checked && (
                <Check className="text-[30px] text-creator-text-sub" />
              )}
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
